// stores/auth.ts
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

interface UserDetails {
    email: string
    id: number
    role: string
    permissions: Permission[]
    default_client_id: number
    multi_clients: boolean
    classification?: string[]
}
interface Permission {
    subject: string
    action: string
}

export const useAuthStore = defineStore('auth', () => {
    const router = useRouter()
    const initialAuth = JSON.parse(localStorage.getItem('auth') || '{}')
    const state = ref<{
        isLoggedIn: boolean
        token: string | null
        userDetails: UserDetails
        expiryDate: number
    }>({
        isLoggedIn: initialAuth.isLoggedIn || false,
        token: initialAuth.token || null,
        userDetails: initialAuth.userDetails || {},
        expiryDate: initialAuth.expiryDate || 0
    })
    function setLoginState(token: string, userDetails: UserDetails) {
        state.value.token = token
        state.value.isLoggedIn = true
        state.value.userDetails = userDetails
        state.value.expiryDate = parseJwt(token).exp

        localStorage.setItem(
            'auth',
            JSON.stringify({
                token,
                isLoggedIn: true,
                userDetails,
                expiryDate: state.value.expiryDate
            })
        )
    }

    async function logout(redirectPath?: string) {
        const currentPath = redirectPath || window.location.pathname + window.location.search

        const storedAuth = JSON.parse(localStorage.getItem('auth') || '{}')
        const rawToken = state.value.token || storedAuth.token
        const token = rawToken?.replace(/^Bearer\s+/i, '') || ''

        const isValidJWT = token && typeof token === 'string' && token.split('.').length === 3

        if (isValidJWT) {
            try {
                await fetch(`${import.meta.env.VITE_API_URL}/auth/logout`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                })
            } catch (error) {
                console.warn('Logout API call failed:', error)
            }
        } else {
            console.warn('Skipping logout: Invalid or missing token.', token)
        }

        state.value.token = null
        state.value.isLoggedIn = false
        state.value.userDetails = {} as UserDetails
        state.value.expiryDate = 0
        localStorage.removeItem('auth')
        localStorage.removeItem('client')
        localStorage.removeItem('group')
        localStorage.removeItem('groups')
        localStorage.removeItem('default_client')

        const savedLoginPreferences = localStorage.getItem('loginPreferences')
        let loginRoute = {
            name: 'Login',
            query: { redirect: currentPath }
        }

        if (savedLoginPreferences) {
            const preferences = JSON.parse(savedLoginPreferences)
            if (preferences.loginType === 'sms' && preferences.rememberMe) {
                loginRoute = {
                    name: 'LoginSMS',
                    query: { redirect: currentPath }
                }
            }

            if (!preferences.rememberMe) {
                localStorage.removeItem('loginPreferences')
            }
        }

        router.push(loginRoute)
    }

    function parseJwt(token: string) {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                })
                .join('')
        )
        return JSON.parse(jsonPayload)
    }

    function hasPermissions({ subject, action,actions }: { subject: string; action: string; actions?: string[] }) {
        const userPermissions = state.value.userDetails.permissions
        if (userPermissions?.find((p) => p.action == 'manage' && p.subject == 'all')) {
            return true
        }
        return !!userPermissions?.find(
            (p) => p.subject == subject && (p.action == action || p.action == 'manage' || (actions && actions.includes(p.action))) 
        )
    }

    function hasAnyAdminPermission(): boolean {
        return (
            hasPermissions({ subject: 'User', action: 'read' }) ||
            hasPermissions({ subject: 'Role', action: 'read' }) ||
            hasPermissions({ subject: 'Client', action: 'read' }) ||
            hasPermissions({ subject: 'NotificationUser', action: 'read' })
        )
    }

    function hasAnyLmsPermission(): boolean {
        return (
            hasPermissions({ subject: 'Course', action: 'read' }) ||
            hasPermissions({ subject: 'Topic', action: 'read' }) ||
            hasPermissions({ subject: 'Track', action: 'read' }) ||
            hasPermissions({ subject: 'Instructor', action: 'read' }) ||
            hasPermissions({ subject: 'Enrollment', action: 'read' })
        )
    }

    return {
        state,
        setLoginState,
        logout,
        hasPermissions,
        hasAnyAdminPermission,
        hasAnyLmsPermission
    }
})
